/**
 * Review Service - Abstracted review data management
 * Calls Rails API for all review operations
 */

import { createReview, validateReview, fromRailsFormat, toRailsFormat } from '../models/Review.js'
import { apiRequest } from '../config/api.js'
import { handleApiError } from './errorHandler.js'


/**
 * Get reviews with pagination
 * @param {Object} options - Pagination and filter options
 * @param {string} [options.courseId] - Optional course ID to filter reviews
 * @param {string} [options.userId] - Optional user ID to filter reviews
 * @param {number} [options.page] - Page number (default: 1)
 * @param {number} [options.perPage] - Items per page (default: 20)
 * @returns {Promise<Object>} Object with data array and pagination info
 */
export async function getReviewsPaginated({ courseId = null, userId = null, page = 1, perPage = 20 } = {}) {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString()
    })
    
    if (courseId) {
      params.append('course_id', courseId)
    }
    if (userId) {
      params.append('user_id', userId)
    }
    
    const endpoint = courseId ? `/courses/${courseId}/reviews?${params.toString()}` : `/reviews?${params.toString()}`
    const data = await apiRequest(endpoint, {
      method: 'GET'
    })
    
    // Handle new paginated format
    if (data.data && data.pagination) {
      return {
        data: data.data.map(review => fromRailsFormat(review)),
        pagination: data.pagination
      }
    }
    
    // Fallback for old format (backward compatibility)
    const reviews = Array.isArray(data) ? data : (data.reviews || [])
    return {
      data: reviews.map(review => fromRailsFormat(review)),
      pagination: {
        page: 1,
        per_page: reviews.length,
        total: reviews.length,
        total_pages: 1
      }
    }
  } catch (error) {
    handleApiError(error)
    throw error
  }
}

/**
 * Get reviews for a specific course
 * @param {string} courseId - Course ID
 * @param {Object} [options] - Pagination options
 * @param {number} [options.page] - Page number (default: 1)
 * @param {number} [options.perPage] - Items per page (default: 20)
 * @returns {Promise<Object>} Object with data array and pagination info, or array for backward compatibility
 */
export async function getReviewsByCourseId(courseId, options = {}) {
  try {
    // If options provided, use paginated version
    if (options.page || options.perPage) {
      return await getReviewsPaginated({ courseId, ...options })
    }
    
    // Otherwise, use nested route (backward compatible)
    const data = await apiRequest(`/courses/${courseId}/reviews`, {
      method: 'GET'
    })
    
    // Handle new paginated format
    if (data.data && data.pagination) {
      return {
        data: data.data.map(review => fromRailsFormat(review)),
        pagination: data.pagination
      }
    }
    
    // Fallback for old format
    const reviews = Array.isArray(data) ? data : (data.reviews || [])
    return reviews.map(review => fromRailsFormat(review))
  } catch (error) {
    if (error.status === 404) {
      return options.page || options.perPage ? { data: [], pagination: { page: 1, per_page: 0, total: 0, total_pages: 0 } } : []
    }
    handleApiError(error)
    throw error
  }
}

/**
 * Get reviews by a specific user
 * @param {string} userId - User ID
 * @param {Object} [options] - Pagination options
 * @param {number} [options.page] - Page number (default: 1)
 * @param {number} [options.perPage] - Items per page (default: 20)
 * @returns {Promise<Object|Array>} Object with data array and pagination info, or array for backward compatibility
 */
export async function getReviewsByUserId(userId, options = {}) {
  try {
    // If options provided, use paginated version
    if (options.page || options.perPage) {
      return await getReviewsPaginated({ userId, ...options })
    }
    
    // Otherwise, backward compatible
    const data = await apiRequest(`/reviews?user_id=${userId}`, {
      method: 'GET'
    })
    
    // Handle new paginated format
    if (data.data && data.pagination) {
      return {
        data: data.data.map(review => fromRailsFormat(review)),
        pagination: data.pagination
      }
    }
    
    // Fallback for old format
    const reviews = Array.isArray(data) ? data : (data.reviews || [])
    return reviews.map(review => fromRailsFormat(review))
  } catch (error) {
    handleApiError(error)
    throw error
  }
}

/**
 * Get a single review by ID
 * @param {string} reviewId - Review ID
 * @returns {Promise<Object|null>} Review object or null if not found
 */
export async function getReviewById(reviewId) {
  try {
    const data = await apiRequest(`/reviews/${reviewId}`, {
      method: 'GET'
    })
    return fromRailsFormat(data)
  } catch (error) {
    if (error.status === 404) {
      return null
    }
    handleApiError(error)
    throw error
  }
}

/**
 * Check if user has already reviewed a course
 * @param {string} courseId - Course ID
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>} Existing review or null
 */
export async function getUserReviewForCourse(courseId, userId) {
  try {
    const reviews = await getReviewsByCourseId(courseId)
    return reviews.find(review => review.userId === userId) || null
  } catch (error) {
    // Silently handle errors for this check - don't show modal
    handleApiError(error, { silent: true })
    return null
  }
}

/**
 * Save a new review
 * @param {Object} reviewData - Review object to save (in camelCase)
 * @returns {Promise<Object>} Saved review object with ID
 */
export async function saveReview(reviewData) {
  // Validate review data
  const reviewToValidate = createReview(reviewData)
  const validation = validateReview(reviewToValidate)
  if (!validation.isValid) {
    throw new Error(`Invalid review data: ${validation.errors.join(', ')}`)
  }

  // Convert to Rails format
  const railsData = toRailsFormat(reviewToValidate)
  // Remove id from create payload (will be generated by backend)
  delete railsData.id
  // user_id will be set by the backend from current_user

  try {
    const data = await apiRequest('/reviews', {
      method: 'POST',
      includeAuth: true,
      body: JSON.stringify({ review: railsData })
    })
    return fromRailsFormat(data)
  } catch (error) {
    handleApiError(error)
    throw error
  }
}

/**
 * Update an existing review
 * @param {string} reviewId - Review ID
 * @param {Object} reviewData - Updated review data (in camelCase)
 * @returns {Promise<Object>} Updated review object
 */
export async function updateReview(reviewId, reviewData) {
  // Validate review data
  const reviewToValidate = createReview({ ...reviewData, id: reviewId })
  const validation = validateReview(reviewToValidate)
  if (!validation.isValid) {
    throw new Error(`Invalid review data: ${validation.errors.join(', ')}`)
  }

  // Convert to Rails format
  const railsData = toRailsFormat(reviewToValidate)
  // Remove id and user_id from update payload (id sent in URL, user_id set by backend)
  delete railsData.id
  delete railsData.user_id

  try {
    const data = await apiRequest(`/reviews/${reviewId}`, {
      method: 'PUT',
      includeAuth: true,
      body: JSON.stringify({ review: railsData })
    })
    return fromRailsFormat(data)
  } catch (error) {
    handleApiError(error)
    throw error
  }
}

/**
 * Delete a review
 * @param {string} reviewId - Review ID
 * @returns {Promise<boolean>} True if deleted successfully
 */
export async function deleteReview(reviewId) {
  try {
    await apiRequest(`/reviews/${reviewId}`, {
      method: 'DELETE',
      includeAuth: true
    })
    return true
  } catch (error) {
    if (error.status === 404) {
      return false
    }
    handleApiError(error)
    throw error
  }
}

